import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { withModifiers as _withModifiers, withKeys as _withKeys, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = ["value"];
import { ref, watch, useId } from 'vue';
import { setError, removeError, removeAllErrors } from '@ts/components/form/utils';
import { ___ } from '@ts/common/language';
export default /*@__PURE__*/ _defineComponent({
    __name: 'PureNumber',
    props: /*@__PURE__*/ _mergeModels({
        errorId: { default: () => useId() },
        separator: {},
        isInteger: { type: Boolean },
        notNull: { type: Boolean }
    }, {
        "value": { required: true },
        "valueModifiers": {},
        "errors": { required: false, default: () => ({}) },
        "errorsModifiers": {},
    }),
    emits: ["update:value", "update:errors"],
    setup(__props) {
        const props = __props;
        const value = _useModel(__props, 'value');
        const errors = _useModel(__props, 'errors');
        const userSeparator = ref('.');
        const userScientific = ref(false);
        const displayValue = ref(formatDisplay(value.value));
        const inputRegex = /^[+-]?(\d+[.,]?\d*|[.,]\d*)?([eE][+-]?\d*)?$/;
        watch(() => value.value, (newVal) => {
            const currentParsed = parseToNumber(displayValue.value);
            if (currentParsed !== newVal) {
                if (newVal !== null) {
                    clearAllErrors();
                }
                const newDisplay = formatDisplay(newVal);
                // console.log('Prop change detected, updating displayValue to:', newDisplay);
                displayValue.value = newDisplay;
            }
        });
        function clearFormatError() {
            removeError(errors, props.errorId, 'format');
        }
        function clearParseError() {
            removeError(errors, props.errorId, 'parse');
        }
        function clearIntegerError() {
            removeError(errors, props.errorId, 'integer');
        }
        function clearNotNullError() {
            removeError(errors, props.errorId, 'not-null');
        }
        function clearAllErrors() {
            removeAllErrors(errors, props.errorId);
        }
        function setFormatError() {
            setError(errors, props.errorId, 'format', ___({ en: 'Invalid number format.', de: 'Ungültiges Zahlenformat.' }));
        }
        function setParseError() {
            setError(errors, props.errorId, 'format', ___({ en: 'Invalid number format.', de: 'Ungültiges Zahlenformat.' }));
        }
        function setIntegerError() {
            setError(errors, props.errorId, 'integer', 'Value must be an integer.');
        }
        function setNotNullError() {
            setError(errors, props.errorId, 'not-null', ___({ en: 'Input cannot be empty.', de: 'Eingabe darf nicht leer sein.' }));
        }
        function parseToNumber(val) {
            if (val === "") {
                clearAllErrors();
                if (props.notNull === true) {
                    setNotNullError();
                }
                return null;
            }
            else {
                clearNotNullError();
            }
            const normalized = val.replace(',', '.');
            if (/^[-+.,eE]+$/.test(normalized) || /[eE][+-]?$/.test(normalized)) {
                setFormatError();
                return null;
            }
            else {
                clearFormatError();
            }
            const parsed = parseFloat(normalized);
            if (isNaN(parsed)) {
                setParseError();
                return null;
            }
            else {
                clearParseError();
            }
            if (props.isInteger && !Number.isInteger(parsed)) {
                setIntegerError();
                return null;
            }
            else {
                clearIntegerError();
            }
            return parsed;
        }
        function formatDisplay(val) {
            if (val === null)
                return "";
            let str;
            if (userScientific.value) {
                str = val.toExponential().replace('e+', 'e');
            }
            else {
                str = val.toString();
            }
            const sep = props.separator ?? userSeparator.value;
            return sep === ',' ? str.replace('.', ',') : str;
        }
        function blockInvalidChar(e) {
            if (e.ctrlKey || e.metaKey || e.key.length > 1)
                return;
            const target = e.target;
            const start = target.selectionStart ?? 0;
            const end = target.selectionEnd ?? 0;
            const newValue = target.value.slice(0, start) + e.key + target.value.slice(end);
            if (!inputRegex.test(newValue)) {
                e.preventDefault();
            }
        }
        function handleInput(e) {
            console.log('handleInput');
            const target = e.target;
            const val = target.value;
            if (!inputRegex.test(val)) {
                target.value = displayValue.value;
                console.log('Input does not match regex, reverting to displayValue:', displayValue.value);
                return;
            }
            if (val !== "") {
                if (/[eE]/.test(val))
                    userScientific.value = true;
                if (!props.separator) {
                    if (val.includes(','))
                        userSeparator.value = ',';
                    else if (val.includes('.'))
                        userSeparator.value = '.';
                }
            }
            else {
                userScientific.value = false;
            }
            displayValue.value = val;
            console.log('Emitting number from handleInput:', parseToNumber(val), userScientific.value, userSeparator.value);
            value.value = parseToNumber(val);
        }
        function onBlur() {
            // console.log('onBlur');
            const currentNum = parseToNumber(displayValue.value);
            if (currentNum === null) {
                // console.log('Emitting number from onBlur:', currentNum);
                // displayValue.value = "";
                if (value.value !== null)
                    value.value = null;
                return;
            }
            const containsExponent = /[eE]/.test(displayValue.value);
            if (!containsExponent && userScientific.value) {
                userScientific.value = false;
            }
            const activeSep = props.separator ?? userSeparator.value;
            const wrongSep = activeSep === ',' ? '.' : ',';
            const hasTrailingJunk = /[.,eE+-]$/.test(displayValue.value);
            const hasWrongSeparator = displayValue.value.includes(wrongSep);
            if (hasTrailingJunk || hasWrongSeparator) {
                displayValue.value = formatDisplay(currentNum);
            }
            if (value.value !== currentNum) {
                // console.log('Emitting number from onBlur:', currentNum);
                value.value = currentNum;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("input", {
                type: "text",
                value: displayValue.value,
                onKeydown: [
                    blockInvalidChar,
                    _cache[0] || (_cache[0] = _withKeys(_withModifiers(() => { }, ["prevent"]), ["enter"]))
                ],
                onInput: handleInput,
                onBlur: onBlur
            }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_1));
        };
    }
});
